/**
 * Procedural art registry: every picture the game shows is drawn in code into a canvas,
 * once, on first use. A key resolves to
 *   - a canvas that behaves like a loaded image (`complete`, `naturalWidth/Height`) for Canvas
 *     2D and Pixi draw sites (`getToonArtCanvas`; frame paths use `peekToonArtCanvas`, which
 *     never draws), and
 *   - a URL (blob, or data URL where blobs are unavailable) for DOM <img> and CSS
 *     (`getToonArtUrl`), whose canvas is also handed to the shared image cache so
 *     `getCachedImage(url)` returns it synchronously.
 *
 * Each spec keeps the pixel size of the PNG it replaces, because some gameplay numbers are
 * derived from image aspect ratios (Fall spawn height, Merge drop height). Every bake of a key
 * is an integer multiple of that w×h, so whichever bake sits in the image cache, the ratios
 * gameplay reads are exactly the same.
 *
 * Memory and boot cost (docs/CARTOON_ART.md §5): a URL request draws the art at `urlScale`
 * only and PNG-encodes that; the 2× canvas is drawn only when a canvas draw site asks for it.
 * Keys drawn only on canvas (`dom: false`) skip the encode, and keys shown only in the DOM
 * (`canvas: false`) keep no canvas at all once encoded.
 *
 * Without a 2D canvas (jsdom tests) a key resolves to the placeholder URL
 * `toon-art/<name>.png` and no canvas.
 */
import { registerReadyImage } from "../../assets/image-cache.js";
import { releaseCanvasTexture } from "./jar/texture-hints.js";

/**
 * @typedef {Object} ToonArtSpec
 * @property {number} w logical width (the replaced PNG's width)
 * @property {number} h logical height
 * @property {number} [scale] backing-store multiple of canvas draw sites (default 2; keep an
 *   integer so ratios stay exact)
 * @property {number} [urlScale] multiple used for the encoded DOM URL (default 1: encoding is the
 *   slow part of boot, and DOM pictures are shown small); the art is redrawn at this scale
 *   rather than downsampled, so it stays crisp
 * @property {number} [cacheScale] multiple of the canvas registered in the image cache under
 *   the URL when no canvas draw site has baked one (default `urlScale`: the encode canvas
 *   itself). Set it to `scale` for a key that is shown in the DOM and also drawn on a canvas
 *   through `getCachedImage(url)`.
 * @property {boolean} [dom] false: drawn only on canvas; the URL is a stable key registered
 *   with the canvas, never PNG-encoded
 * @property {boolean} [canvas] false: shown only in the DOM; nothing is registered in the image
 *   cache and no canvas is kept after the encode
 * @property {(c: CanvasRenderingContext2D, w: number, h: number) => void} draw draws in logical units
 * @property {"blob"|"dataurl"} [url] URL kind (CSS cursors want a data URL)
 * @property {string} [name] file-like name used in URL fragments and placeholders
 */

/** @type {Record<string, ToonArtSpec>} */
var SPECS = Object.create(null);
/** Canvases baked for canvas draw sites (key → canvas, or null without a 2D canvas). */
var CANVASES = Object.create(null);
/** Backing-store multiple of each CANVASES entry. */
var BAKED_SCALE = Object.create(null);
/** @type {Record<string, string>} */
var URLS = Object.create(null);
/** The canvas registered in the image cache under URLS[key], and its multiple. */
var URL_CANVAS = Object.create(null);
var URL_CANVAS_SCALE = Object.create(null);

/** A replaced canvas may still be on screen this frame; free its GPU texture a moment later. */
var RETIRE_DELAY_MS = 1000;

/**
 * Define (or replace) the art for `key`. Replacing drops any cached canvas/URL.
 * @param {string} key
 * @param {ToonArtSpec} spec
 */
export function defineToonArt(key, spec) {
  SPECS[key] = spec;
  delete CANVASES[key];
  delete BAKED_SCALE[key];
  delete URLS[key];
  delete URL_CANVAS[key];
  delete URL_CANVAS_SCALE[key];
}

/** @param {string} key */
export function getToonArtSpec(key) {
  return SPECS[key] || null;
}

function canDraw() {
  if (typeof document === "undefined") return false;
  try {
    var probe = document.createElement("canvas");
    return !!(probe.getContext && probe.getContext("2d"));
  } catch (_e) {
    return false;
  }
}
var drawable = null;

function isDrawable() {
  if (drawable == null) drawable = canDraw();
  return drawable;
}

function fullScale(spec) {
  return spec.scale || 2;
}

/**
 * The drawn canvas for `key` (drawn at the spec's `scale` on first call), or null without a 2D
 * canvas. A key already baked at another scale (`bakeToonArtCanvas`) returns that bake.
 * The canvas carries `complete`, `naturalWidth` and `naturalHeight` like a decoded image.
 * @param {string} key
 * @returns {HTMLCanvasElement|null}
 */
export function getToonArtCanvas(key) {
  if (key in CANVASES) return CANVASES[key];
  var spec = SPECS[key];
  if (!spec) return null;
  return bake(key, spec, fullScale(spec));
}

/**
 * The canvas for `key` if it is already drawn, else null. Never draws: frame paths call this
 * and queue the bake for idle time (see `queueToonArtWarm`) instead of drawing in a frame.
 * @param {string} key
 * @returns {HTMLCanvasElement|null}
 */
export function peekToonArtCanvas(key) {
  return CANVASES[key] || null;
}

/** Backing-store multiple of the drawn canvas for `key` (0 when not drawn). */
export function getToonArtCanvasScale(key) {
  return CANVASES[key] ? BAKED_SCALE[key] : 0;
}

/**
 * Draw `key` at backing-store multiple `scale` unless it is already drawn at that scale or
 * finer. A finer bake replaces a coarser one (its GPU texture is freed shortly after), so
 * art drawn small can be baked small and sharpened later when it is shown bigger.
 * @param {string} key
 * @param {number} [scale] default: the spec's `scale`
 * @returns {HTMLCanvasElement|null}
 */
export function bakeToonArtCanvas(key, scale) {
  var spec = SPECS[key];
  if (!spec) return null;
  var s = scale > 0 ? scale : fullScale(spec);
  if (key in CANVASES && (CANVASES[key] === null || BAKED_SCALE[key] >= s)) return CANVASES[key];
  return bake(key, spec, s);
}

function bake(key, spec, s) {
  if (!isDrawable()) {
    CANVASES[key] = null;
    return null;
  }
  var cv = markImageLike(renderSpec(key, spec, s));
  var old = CANVASES[key];
  CANVASES[key] = cv;
  BAKED_SCALE[key] = s;
  // A URL registered with a coarser bake now resolves to this one (same aspect ratio).
  if (URLS[key] && URL_CANVAS[key] && URL_CANVAS_SCALE[key] < s) {
    var prev = URL_CANVAS[key];
    registerReadyImage(URLS[key], cv);
    URL_CANVAS[key] = cv;
    URL_CANVAS_SCALE[key] = s;
    if (prev !== old) retire(prev);
  }
  if (old && old !== cv) retire(old);
  return cv;
}

function retire(cv) {
  if (!cv) return;
  if (typeof setTimeout === "function") {
    setTimeout(function () { releaseCanvasTexture(cv); }, RETIRE_DELAY_MS);
  } else {
    releaseCanvasTexture(cv);
  }
}

function renderSpec(key, spec, s, readBack) {
  var cv = document.createElement("canvas");
  cv.width = Math.round(spec.w * s);
  cv.height = Math.round(spec.h * s);
  // A picture drawn only to be PNG-encoded asks for a CPU-backed canvas: toDataURL on a
  // GPU-rasterised canvas is a synchronous GPU flush and readback (a long stall on phones).
  var c = readBack ? cv.getContext("2d", { willReadFrequently: true }) : cv.getContext("2d");
  if (!c) c = cv.getContext("2d");
  c.scale(s, s);
  try {
    spec.draw(c, spec.w, spec.h);
  } catch (err) {
    if (typeof console !== "undefined") console.error("[toon-art] " + key + " failed", err);
  }
  return cv;
}

/** Give a canvas the fields draw sites read from a decoded HTMLImageElement. */
export function markImageLike(cv) {
  try {
    cv.complete = true;
    cv.naturalWidth = cv.width;
    cv.naturalHeight = cv.height;
  } catch (_e) { /* read-only in exotic hosts: draw sites fall back */ }
  return cv;
}

function artName(key) {
  var spec = SPECS[key];
  return (spec && spec.name) || key.split("/").pop();
}

/** Placeholder URL without a 2D canvas, and the stable URL of canvas-only (`dom: false`) art. */
function placeholderUrl(key) {
  return "toon-art/" + artName(key) + ".png";
}

function dataUrlToBlobUrl(dataUrl) {
  if (typeof URL === "undefined" || typeof URL.createObjectURL !== "function" || typeof Blob === "undefined") {
    return null;
  }
  var comma = dataUrl.indexOf(",");
  var meta = dataUrl.slice(5, comma);
  var mime = meta.split(";")[0] || "image/png";
  var bin = atob(dataUrl.slice(comma + 1));
  var bytes = new Uint8Array(bin.length);
  for (var i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return URL.createObjectURL(new Blob([bytes], { type: mime }));
}

function encodeUrl(key, spec, cv) {
  var dataUrl = "";
  try {
    dataUrl = cv.toDataURL("image/png");
  } catch (_e) {
    dataUrl = "";
  }
  if (!dataUrl || dataUrl.indexOf("data:") !== 0) return "";
  if (spec.url === "dataurl") return dataUrl;
  return (dataUrlToBlobUrl(dataUrl) || dataUrl) + "#" + artName(key) + ".png";
}

/**
 * A stable URL for `key` (memoised). Unless the spec says `canvas: false`, a canvas of the
 * art is registered in the shared image cache under this URL, so `getCachedImage(url)` returns
 * it at once: the canvas draw sites' bake when there is one, else the `cacheScale` bake.
 * @param {string} key
 * @returns {string}
 */
export function getToonArtUrl(key) {
  if (URLS[key]) return URLS[key];
  var spec = SPECS[key];
  if (!spec) return "";
  var url = "";
  var reg = null;
  var regScale = 0;
  if (isDrawable()) {
    if (spec.dom === false) {
      reg = getToonArtCanvas(key);
      regScale = BAKED_SCALE[key] || 0;
      url = placeholderUrl(key);
    } else {
      var enc = encodeCanvas(key, spec);
      url = encodeUrl(key, spec, enc);
      return publishUrl(key, spec, url, enc);
    }
  }
  if (!url) url = placeholderUrl(key);
  if (reg) {
    registerReadyImage(url, reg);
    URL_CANVAS[key] = reg;
    URL_CANVAS_SCALE[key] = regScale;
  }
  URLS[key] = url;
  return url;
}

/** The canvas a DOM URL is encoded from: the art at `urlScale` (a canvas bake at that scale is reused). */
function encodeCanvas(key, spec) {
  var us = spec.urlScale || 1;
  return CANVASES[key] && BAKED_SCALE[key] === us ? CANVASES[key] : renderSpec(key, spec, us, true);
}

/**
 * Memoise the encoded URL of a DOM picture and, unless the spec says `canvas: false`, register a
 * canvas of it in the image cache under that URL (see `getToonArtUrl`).
 */
function publishUrl(key, spec, url, enc) {
  if (!url) url = placeholderUrl(key);
  if (spec.canvas !== false) {
    var us = spec.urlScale || 1;
    var cs = spec.cacheScale || us;
    var reg;
    var regScale;
    if (CANVASES[key]) {
      reg = CANVASES[key];
      regScale = BAKED_SCALE[key];
    } else if (cs === us) {
      reg = markImageLike(enc);
      regScale = us;
    } else {
      reg = bakeToonArtCanvas(key, cs);
      regScale = cs;
    }
    if (reg) {
      registerReadyImage(url, reg);
      URL_CANVAS[key] = reg;
      URL_CANVAS_SCALE[key] = regScale;
    }
  }
  URLS[key] = url;
  return url;
}

function canEncodeAsync(spec) {
  return spec.dom !== false && spec.url !== "dataurl"
    && typeof URL !== "undefined" && typeof URL.createObjectURL === "function"
    && typeof HTMLCanvasElement !== "undefined" && typeof HTMLCanvasElement.prototype.toBlob === "function";
}

/**
 * Resolve `getToonArtUrl(key)` ahead of need with the PNG encode handed to the browser
 * (`canvas.toBlob`, which encodes in the background instead of one long synchronous
 * `toDataURL` task). The picture is drawn now; the URL is memoised when the encode lands (a
 * synchronous `getToonArtUrl` before that still works, and wins). Without async encoding this
 * is the synchronous path.
 * @param {string} key
 * @returns {Promise<string>}
 */
export function warmToonArtUrl(key) {
  if (URLS[key]) return Promise.resolve(URLS[key]);
  var spec = SPECS[key];
  if (!spec) return Promise.resolve("");
  if (!isDrawable() || !canEncodeAsync(spec)) return Promise.resolve(getToonArtUrl(key));
  var enc = encodeCanvas(key, spec);
  return new Promise(function (resolve) {
    function fallback() {
      resolve(URLS[key] || getToonArtUrl(key));
    }
    try {
      enc.toBlob(function (blob) {
        if (URLS[key]) {
          resolve(URLS[key]);
          return;
        }
        if (!blob) {
          fallback();
          return;
        }
        var url = "";
        try {
          url = URL.createObjectURL(blob) + "#" + artName(key) + ".png";
        } catch (_e) {
          url = "";
        }
        if (!url) {
          fallback();
          return;
        }
        resolve(publishUrl(key, spec, url, enc));
      }, "image/png");
    } catch (_e) {
      fallback();
    }
  });
}

/** Whether `getToonArtUrl(key)` is already resolved (no draw or encode left to do). */
export function hasToonArtUrl(key) {
  return !!URLS[key];
}

// ---- idle-time warming -----------------------------------------------------------------

/**
 * Bakes and encodes queued for idle time, one per idle callback, so no frame pays for drawing
 * a picture. A gate can hold the queue (the sky's first-countdown FPS probe must not see this
 * work); an uploader can hand each baked canvas to the GPU right after it is drawn, so the
 * texture upload (and its mip chain) does not land on the frame that first shows it.
 */
var warmQueue = [];
var warmIds = Object.create(null);
var warmScheduled = false;
var warmGate = null;
var warmUploader = null;
var WARM_IDLE_TIMEOUT_MS = 400;
var WARM_GATE_RETRY_MS = 250;
var WARM_FALLBACK_MS = 30;

/**
 * Queue `fn` for idle time; a job with the same `id` already waiting is not queued twice.
 * `fn` may return a canvas (or an array of canvases) to pre-upload.
 * @param {string} id
 * @param {() => (HTMLCanvasElement|HTMLCanvasElement[]|null|undefined)} fn
 */
export function queueToonArtWarm(id, fn) {
  if (typeof fn !== "function") return;
  if (id && warmIds[id]) return;
  if (id) warmIds[id] = true;
  warmQueue.push({ id: id, fn: fn });
  scheduleWarm(0);
}

/** @param {(() => boolean)|null} fn returns false while the queue must wait */
export function setToonArtWarmGate(fn) {
  warmGate = typeof fn === "function" ? fn : null;
}

/** @param {((canvas: HTMLCanvasElement) => void)|null} fn pre-uploads one baked canvas */
export function setToonArtWarmUploader(fn) {
  warmUploader = typeof fn === "function" ? fn : null;
}

function gateOpen() {
  if (!warmGate) return true;
  try {
    return warmGate() !== false;
  } catch (_e) {
    return true;
  }
}

function scheduleWarm(delayMs) {
  if (warmScheduled || !warmQueue.length) return;
  warmScheduled = true;
  if (!(delayMs > 0) && typeof requestIdleCallback === "function") {
    requestIdleCallback(runWarm, { timeout: WARM_IDLE_TIMEOUT_MS });
  } else if (typeof setTimeout === "function") {
    setTimeout(runWarm, delayMs > 0 ? delayMs : WARM_FALLBACK_MS);
  } else {
    warmScheduled = false;
  }
}

function upload(out) {
  if (!out || !warmUploader) return;
  var list = Array.isArray(out) ? out : [out];
  for (var i = 0; i < list.length; i++) {
    if (!list[i] || typeof list[i] !== "object") continue;
    try {
      warmUploader(list[i]);
    } catch (_e) { /* the first draw uploads it instead */ }
  }
}

function runWarm() {
  warmScheduled = false;
  if (!gateOpen()) {
    scheduleWarm(WARM_GATE_RETRY_MS);
    return;
  }
  // One job per slice: a creature layer or an encode is a few milliseconds of 2D drawing.
  var job = warmQueue.shift();
  if (job) {
    if (job.id) delete warmIds[job.id];
    var out = null;
    try {
      out = job.fn();
    } catch (err) {
      if (typeof console !== "undefined") console.error("[toon-art] warm " + job.id + " failed", err);
    }
    upload(out);
  }
  scheduleWarm(0);
}

/** @internal Test helper: run every queued warm job now (ignores the gate). */
export function __flushToonArtWarmForTests() {
  while (warmQueue.length) {
    var job = warmQueue.shift();
    if (job.id) delete warmIds[job.id];
    upload(job.fn());
  }
}

/** @internal Test helper: forget drawn canvases, URLs and queued work (specs stay). */
export function __resetToonArtCacheForTests() {
  CANVASES = Object.create(null);
  BAKED_SCALE = Object.create(null);
  URLS = Object.create(null);
  URL_CANVAS = Object.create(null);
  URL_CANVAS_SCALE = Object.create(null);
  warmQueue = [];
  warmIds = Object.create(null);
  warmScheduled = false;
  warmGate = null;
  warmUploader = null;
  drawable = null;
}
